/**
 * Baby sleep schedules — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_home_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const babySleepSchedulesPack: ToilePack = {
    slug: 'baby-sleep-schedules',
    category: 'classroom',
    name: 'Baby sleep schedules',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'plan', label: 'Daily plan', subject: 'a daily plan titled "Baby Sleep Schedule" with rows "Wake up", "Nap 1", "Nap 2", "Nap 3" and "Bedtime", an empty time box beside each and a small moon and cloud'},
        {key: 'log', label: 'Sleep log', subject: 'a grid titled "Sleep Log" with twenty-four hour columns marked "12am" to "11pm" across the top and seven rows "Mon" to "Sun", every hour box empty to shade'},
        {key: 'routine', label: 'Bedtime steps', subject: 'a soft card titled "Baby\'s Bedtime" with five numbered object icons captioned "Bath", "Massage", "Feed", "Song" and "Sleep"'},
    ],
    styles: [
        {key: 'dusk', label: 'Soft dusk', style: 'gentle flat chart with a small moon and cloud motif, rounded boxes and thin lines', palette: 'dusty blue, lavender and cream', framing: 'centered on a cream page with an even margin'},
        {key: 'neutral', label: 'Neutral minimal', style: 'minimal chart with fine lines, plenty of white space and tiny star icons', palette: 'warm gray, sand and ivory', framing: 'centered on an ivory page with an even margin'},
        {key: 'mono', label: 'Black and white', style: 'clean black outline printable with uniform bold lines, plain boxes and simple moon and star icons, nothing filled', palette: 'black ink only', framing: 'centered on a white page with an even margin'},
    ],
    directions: ['by age', 'with feeds', 'for twins'],
    directionHint: 'try: by age',
    seo: {
        title: 'Baby Sleep Schedules: Daily Plan, Sleep Log, Bedtime Steps',
        metaDescription: 'Nine printable baby sleep schedules: a daily nap plan, an hour-by-hour sleep log and a picture bedtime routine.',
        h1: 'Baby sleep schedule: daily nap plan, sleep log, bedtime steps',
        tagline: 'Nine printable pages: three charts, each in soft dusk, neutral minimal and black and white.',
        intro: 'Printable baby sleep schedules to plan naps and bedtime and to see how sleep changes from week to week. The three charts are a daily plan with rows for wake up, three naps and bedtime; a seven-day log with twenty-four hour columns to shade; and a picture bedtime routine with five steps from bath to sleep. Each comes in three styles.',
        useCases: [
            'a nap plan on the nursery wall',
            'a week of sleep records for a doctor',
            'a picture bedtime routine for a baby',
        ],
        faq: [
            {q: 'What is on the daily plan?', a: 'Rows for wake up, nap one, nap two, nap three and bedtime, each with an empty time box beside it.'},
            {q: 'How does the sleep log work?', a: 'Seven rows from Monday to Sunday with hour columns from 12am to 11pm, and every box empty to shade.'},
            {q: 'What are the bedtime steps?', a: 'Bath, massage, feed, song and sleep, each with an object icon.'},
            {q: 'Which style is calm for a nursery?', a: 'Neutral minimal uses fine lines in warm gray, sand and ivory; soft dusk uses dusty blue and lavender.'},
        ],
    },
}
